/**
 * Accessibility utilities and helpers
 */

// ARIA live region announcer for screen readers
export const announceToScreenReader = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcer = document.createElement('div')
    announcer.setAttribute('aria-live', priority)
    announcer.setAttribute('aria-atomic', 'true')
    announcer.className = 'sr-only'
    announcer.textContent = message

    document.body.appendChild(announcer)

    // Remove after announcement
    setTimeout(() => {
        document.body.removeChild(announcer)
    }, 1000)
}

// Focus management utilities
export const trapFocus = (element: HTMLElement) => {
    const focusableElements = element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as NodeListOf<HTMLElement>

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return

        if (e.shiftKey) {
            if (document.activeElement === firstElement) {
                lastElement.focus()
                e.preventDefault()
            }
        } else {
            if (document.activeElement === lastElement) {
                firstElement.focus()
                e.preventDefault()
            }
        }
    }

    element.addEventListener('keydown', handleTabKey)

    // Return cleanup function
    return () => {
        element.removeEventListener('keydown', handleTabKey)
    }
}

// Skip link functionality
export const createSkipLink = (targetId: string, text: string = 'Skip to main content') => {
    const skipLink = document.createElement('a')
    skipLink.href = `#${targetId}`
    skipLink.textContent = text
    skipLink.className = 'skip-link'
    skipLink.addEventListener('click', (e) => {
        e.preventDefault()
        const target = document.getElementById(targetId)
        if (target) {
            target.focus()
            target.scrollIntoView()
        }
    })

    return skipLink
}

// Keyboard navigation helpers
export const handleArrowKeyNavigation = (
    e: KeyboardEvent,
    items: HTMLElement[],
    currentIndex: number,
    onIndexChange: (newIndex: number) => void,
    orientation: 'horizontal' | 'vertical' = 'horizontal'
) => {
    const { key } = e
    let newIndex = currentIndex

    if (orientation === 'horizontal') {
        if (key === 'ArrowLeft') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
        } else if (key === 'ArrowRight') {
            newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
        }
    } else {
        if (key === 'ArrowUp') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
        } else if (key === 'ArrowDown') {
            newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
        }
    }

    if (newIndex !== currentIndex) {
        e.preventDefault()
        onIndexChange(newIndex)
        items[newIndex]?.focus()
    }
}

// Color contrast utilities
export const getContrastRatio = (color1: string, color2: string): number => {
    const getLuminance = (color: string): number => {
        const rgb = color.match(/\d+/g)?.map(Number) || [0, 0, 0]
        const [r, g, b] = rgb.map(c => {
            c = c / 255
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
        })
        return 0.2126 * r + 0.7152 * g + 0.0722 * b
    }

    const lum1 = getLuminance(color1)
    const lum2 = getLuminance(color2)
    const brightest = Math.max(lum1, lum2)
    const darkest = Math.min(lum1, lum2)

    return (brightest + 0.05) / (darkest + 0.05)
}

// Reduced motion detection
export const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// High contrast detection
export const prefersHighContrast = (): boolean => {
    return window.matchMedia('(prefers-contrast: high)').matches
}

// Generate unique IDs for form elements
export const generateId = (prefix: string = 'id'): string => {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
}

// ARIA describedby helper
export const createAriaDescribedBy = (ids: (string | undefined)[]): string | undefined => {
    const validIds = ids.filter(Boolean)
    return validIds.length > 0 ? validIds.join(' ') : undefined
}

// Focus restoration
export class FocusManager {
    private previousFocus: HTMLElement | null = null

    saveFocus() {
        this.previousFocus = document.activeElement as HTMLElement
    }

    restoreFocus() {
        if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
            this.previousFocus.focus()
        }
    }
}

// Accessible modal/dialog utilities
export const createAccessibleModal = (modalElement: HTMLElement) => {
    const focusManager = new FocusManager()

    const open = () => {
        focusManager.saveFocus()
        modalElement.setAttribute('aria-hidden', 'false')
        modalElement.setAttribute('role', 'dialog')
        modalElement.setAttribute('aria-modal', 'true')

        // Focus first focusable element
        const firstFocusable = modalElement.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement
        firstFocusable?.focus()

        // Trap focus
        const cleanup = trapFocus(modalElement)

        // Handle escape key
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close()
            }
        }

        document.addEventListener('keydown', handleEscape)

        return () => {
            cleanup()
            document.removeEventListener('keydown', handleEscape)
        }
    }

    const close = () => {
        modalElement.setAttribute('aria-hidden', 'true')
        modalElement.removeAttribute('role')
        modalElement.removeAttribute('aria-modal')
        focusManager.restoreFocus()
    }

    return { open, close }
}